(function () {
  // ---------- small helpers ----------
  const q  = (r, s) => r.querySelector(s);
  const qa = (r, s) => Array.from(r.querySelectorAll(s));
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  // ---------- DOM mapping for both section types ----------
  function getCarouselParts(root) {
    const isCollection = root.classList.contains('list-collections');

    // Scroller element that actually scrolls horizontally
    const scroller =
      q(root, '.list-collections__scroller') ||
      q(root, 'scrollable-content') ||
      q(root, '.scroller');

    // Track that holds the items in a row
    const track = isCollection
      ? q(root, '.list-collections__item-list')
      : q(root, '.product-list__inner');

    // Item selector
    const itemSel = isCollection ? '.list-collections__item' : '.product-item';

    // Arrow buttons (theme renders these)
    const prevBtn = q(root, '.prev-next-button--prev');
    const nextBtn = q(root, '.prev-next-button--next');

    return { isCollection, scroller, track, itemSel, prevBtn, nextBtn };
  }

  // ---------- sizing + overflow ----------
  function getGapPx(el) {
    if (!el) return 0;
    const cs = getComputedStyle(el);
    // theme sometimes sets either columnGap or gap
    return parseFloat(cs.columnGap || cs.gap || 0) || 0;
  }

  function itemWidth(track, itemSel) {
    const first = q(track, itemSel);
    if (!first) return 0;
    const r = first.getBoundingClientRect();
    return r.width;
  }

  function totalWidth(track, itemSel) {
    const items = qa(track, itemSel);
    const gap = getGapPx(track);
    const w = items.length ? itemWidth(track, itemSel) : 0;
    return items.length ? (items.length * w + (items.length - 1) * gap) : 0;
  }

  function canOverflow(scroller, track, itemSel) {
    if (!scroller || !track) return false;
    return totalWidth(track, itemSel) - scroller.getBoundingClientRect().width > 1;
  }

  function visibleCount(scroller, track, itemSel) {
    const w = itemWidth(track, itemSel);
    if (!w) return 1;
    const gap = getGapPx(track);
    const vw = scroller.getBoundingClientRect().width;
    // round to the nearest visible whole card count
    return clamp(Math.round((vw + gap) / (w + gap)), 1, 50);
  }

  function stepSize(track, itemSel) {
    const w = itemWidth(track, itemSel);
    const gap = getGapPx(track);
    return w ? (w + gap) : 0;
  }

  // ---------- main per-carousel controller ----------
  function LoopCarousel(root) {
    const parts = getCarouselParts(root);
    const { isCollection, scroller, track, itemSel, prevBtn, nextBtn } = parts;
    if (!scroller || !track) return;

    let ro;                    // resize observer
    let overflow = false;      // whether content overflows
    let step = 0;              // pixel step for prev/next
    let reinitQueued = false;  // debounce init
    let stepping = false;      // suppress wrap during programmatic jumps
    let cloneCount = 0;        // how many items cloned at each end
    let originalCount = 0;     // number of real items

    // theme sometimes uses scroll-snap; disable it for reliable loop
    scroller.style.scrollSnapType = 'none';

    function realItems() {
      return qa(track, itemSel).filter(n => n.dataset.clone !== '1');
    }

    function stripClones() {
      qa(track, `${itemSel}[data-clone="1"]`).forEach(n => n.remove());
      cloneCount = 0;
    }

    function makeClones(n) {
      const items = realItems();
      if (!items.length) return;
      // if very few items relative to visible, cloning makes no sense; skip
      const minNeeded = Math.min(items.length, n);
      const head = items.slice(0, minNeeded).map(el => { const c = el.cloneNode(true); c.dataset.clone = '1'; return c; });
      const tail = items.slice(-minNeeded).map(el => { const c = el.cloneNode(true); c.dataset.clone = '1'; return c; });
      tail.forEach(c => track.insertBefore(c, items[0]));
      head.forEach(c => track.appendChild(c));
      cloneCount = minNeeded;
    }

    function contentWidthOriginal() {
      // compute width of the *real* sequence (used for wrap jump)
      const items = realItems();
      if (!items.length) return 0;
      const w = itemWidth(track, itemSel);
      const gap = getGapPx(track);
      return items.length * w + (items.length - 1) * gap;
    }

    function setArrowY() {
      // Center arrows to first media box
      const mediaSel = isCollection
        ? '.list-collections__media'
        : '.product-item__media, .card__media, .media';

      const media = q(track, mediaSel);
      if (!media) return;
      const mr = media.getBoundingClientRect();
      const rr = root.getBoundingClientRect();
      const centerY = (mr.top - rr.top) + (mr.height / 2);
      root.style.setProperty('--arrow-center', `${centerY}px`);
    }

    function toggleArrows(on) {
      root.setAttribute('data-can-scroll', on ? 'true' : 'false');
      if (prevBtn) prevBtn.disabled = !on;
      if (nextBtn) nextBtn.disabled = !on;
    }

    function jumpForwardByOriginal() {
      stepping = true;
      scroller.scrollLeft += contentWidthOriginal();
      requestAnimationFrame(() => { stepping = false; });
    }

    function jumpBackByOriginal() {
      stepping = true;
      scroller.scrollLeft -= contentWidthOriginal();
      requestAnimationFrame(() => { stepping = false; });
    }

    function onScrollWrap() {
      if (!overflow || stepping || !cloneCount) return;
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      // a small threshold prevents jitter near edges
      const threshold = Math.max(8, step / 4);
      if (scroller.scrollLeft <= threshold) {
        // inside head clones → jump forward by one original span
        jumpForwardByOriginal();
      } else if (maxScroll - scroller.scrollLeft <= threshold) {
        // inside tail clones → jump backward by one original span
        jumpBackByOriginal();
      }
    }

    // prevent theme's own arrow handlers from double-scrolling
    function swallowArrow(e) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    }

    function bindButtons() {
      if (prevBtn) {
        prevBtn.addEventListener('click', swallowArrow, true); // capture: true
        prevBtn.addEventListener('click', () => {
          if (!overflow) return;
          scroller.scrollBy({ left: -step, behavior: 'smooth' });
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', swallowArrow, true);
        nextBtn.addEventListener('click', () => {
          if (!overflow) return;
          scroller.scrollBy({ left: step, behavior: 'smooth' });
        });
      }
    }

    function queueInit() {
      if (reinitQueued) return;
      reinitQueued = true;
      requestAnimationFrame(() => {
        reinitQueued = false;
        init();
      });
    }

    function init() {
      // measure
      overflow = canOverflow(scroller, track, itemSel);
      toggleArrows(overflow);

      // reset + compute step
      stripClones();
      step = stepSize(track, itemSel);
      originalCount = realItems().length;

      if (overflow && step > 0 && originalCount > 0) {
        // how many items are visible right now?
        const vis = visibleCount(scroller, track, itemSel);

        // only loop if we truly overflow and have enough items
        // cloning up to 'vis' at both ends keeps scroll seamless
        makeClones(vis);

        // start user at first real item (skip tail clones)
        const oldBehavior = scroller.style.scrollBehavior;
        scroller.style.scrollBehavior = 'auto';
        scroller.scrollLeft = step * vis;
        scroller.style.scrollBehavior = oldBehavior || '';
      } else {
        // no overflow → no arrows, no clones, scroll to start
        const oldBehavior = scroller.style.scrollBehavior;
        scroller.style.scrollBehavior = 'auto';
        scroller.scrollLeft = 0;
        scroller.style.scrollBehavior = oldBehavior || '';
      }

      setArrowY();

      // observers / events
      if (ro) ro.disconnect();
      ro = new ResizeObserver(() => queueInit());
      ro.observe(scroller);
      ro.observe(track);

      qa(track, 'img').forEach(img => {
        if (!img.complete) {
          img.addEventListener('load', setArrowY, { once: true });
          img.addEventListener('error', setArrowY, { once: true });
        }
      });

      scroller.removeEventListener('scroll', onScrollWrap);
      scroller.addEventListener('scroll', onScrollWrap, { passive: true });

      bindButtons();
    }

    init();
  }

  function bootAll() {
    qa(document, '[data-infinite-when-overflow]').forEach(root => LoopCarousel(root));
  }

  // initial + dynamic mounts
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }
  document.addEventListener('shopify:section:load', bootAll);
  document.addEventListener('shopify:section:unload', bootAll);
})();
